import React, { useMemo } from 'react'
import { useState } from 'react'
import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Form,
  Col,
  Row,
  Button,
  Dropdown,
  useToaster,
  CenteredSpinner
} from 'react-angle-dashboard-components'
import useStep from '../../hooks/use-step'
import { Employee } from '../../interfaces/employee'
import { GbsStore } from '../../store'
import { GbsStep } from '../../store/interfaces'
import {
  Amount,
  EmployeesWrapper,
  ListHeader,
  ListWrapper,
  Title,
  Subtitle,
  Highlight,
  SwitchButton
} from './styles'
import {
  EmployeeWrapper,
  EmployeeFirstRow,
  EmployeeNumber,
  EmployeeInput,
  InputLabel,
  EmployeeSecondRow
} from '../../components/Employee'
import { InfoIcon, InfoP, InfoWrapper } from '../../components/Info'
// import { Actions } from '../../store/actions'
import { employeeService } from '../../services'
import { useEmployees } from '../../hooks/use-employees'
import { Paths } from '../../routes/paths'
import { useInit } from '../../hooks/use-init'
import { useZipCode } from '../../store/selectors'

const stepKey = 'employees'

const allEmployeesFilled = (
  employees: Employee[],
  isUsingDOB: boolean
): boolean => {
  if (employees.length === 0) return false

  return employees.reduce<boolean>((acc: boolean, e: Employee) => {
    if (isUsingDOB) {
      return (
        (e.spouse ? Boolean(e.dob && e.spouseDob) : Boolean(e.dob)) &&
        Boolean(e.children || e.children === 0)
      )
    }

    const isValid =
      (e.spouse ? Boolean(e.age && e.spouseAge) : Boolean(e.age)) &&
      Boolean(e.children || e.children === 0)

    return acc && isValid
  }, true)
}

export const Employees = () => {
  const history = useHistory()
  const { show } = useToaster()
  const { dispatch } = useContext(GbsStore)
  const zipCode = useZipCode()

  const { updateStoreWithBE } = useInit(dispatch, zipCode)

  const {
    employees,
    isLoadingEmployees,
    handleEmployeeChange,
    handleAddEmployee,
    handleDelete,
    isUsingDOB,
    handleDOBSwitch
  } = useEmployees(employeeService)

  const [isCreatingEmployees, setIsCreatingEmployees] = useState<boolean>(false)

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    const saveEmployeesToBE = () => {
      if (
        isUsingDOB &&
        employees.some((e) => e.dob && new Date(e.dob) >= new Date())
      ) {
        return show({
          message: 'Date of birth must be before today',
          type: 'danger'
        })
      }

      setIsCreatingEmployees(true)
      employeeService
        .createEmployees(employees, isUsingDOB)
        .then((r) => {
          history.push(Paths.yourPlans)
          updateStoreWithBE()
        })
        .catch((e: any) => {
          show({
            message: e.message || 'Failed to create employees',
            type: 'danger',
            icon: 'icon-cancel'
          })
        })
        .finally(() => setIsCreatingEmployees(false))
    }

    return {
      bottomBar: {
        buttons: [
          {
            text: 'Next',
            variant: 'primary',
            disabled: !allEmployeesFilled(employees, isUsingDOB),
            callback: saveEmployeesToBE
          }
        ],
        staticEmployeesAndAge: true
      },
      topBar: {
        completedPercentage: 80,
        showEdit: true,
        backCallback: () => history.push(Paths.recommendedPlans)
      }
    }
  }, [history, isUsingDOB, show, employees, updateStoreWithBE])

  useStep(stepKey, stepConfig, dispatch)

  const MenuToggle = React.forwardRef(({ onClick }: any, ref: any) => (
    // eslint-disable-next-line
    <a
      href=""
      ref={ref}
      onClick={(e) => {
        e.preventDefault()
        onClick(e)
      }}
    >
      <i
        className="icon icon-more text-secondary"
        style={{ fontSize: 14, height: 14, width: 14 }}
      />
    </a>
  ))

  if (isCreatingEmployees || isLoadingEmployees) {
    return <CenteredSpinner />
  }

  return (
    <EmployeesWrapper>
      <Title>Your Employees</Title>
      <Subtitle>List your current employees</Subtitle>
      <InfoWrapper>
        <InfoIcon>!</InfoIcon>
        <InfoP>
          In order to get the most accurate pricing for your final premiums,
          please include the age of both your employees and their particiating
          spouse along with participating children.
        </InfoP>
      </InfoWrapper>
      <ListWrapper>
        <ListHeader>
          <h3>
            <Highlight>Employees: </Highlight>
            <Amount>{employees.length}</Amount>
          </h3>
          <div className="d-flex">
            <SwitchButton
              left
              active={!isUsingDOB}
              onClick={() => handleDOBSwitch(false)}
            >
              Age
            </SwitchButton>
            <SwitchButton
              active={isUsingDOB}
              onClick={() => handleDOBSwitch(true)}
            >
              Date of Birth
            </SwitchButton>
          </div>
        </ListHeader>
        {employees.map((employee, i) => (
          <EmployeeWrapper key={employee.id}>
            <Row className="align-items-center d-flex">
              <Col>
                <EmployeeFirstRow>
                  <Col lg={5} md={6} className="mb-3 m-lg-0">
                    <div className="d-flex align-items-baseline">
                      <EmployeeNumber className="mr-1">Employee</EmployeeNumber>
                      <EmployeeNumber className="mr-3">
                        #{employee.id}
                      </EmployeeNumber>
                      <EmployeeInput
                        type="text"
                        placeholder="First Name (optional)"
                        className="bg-white h-100 w-100"
                        value={employee.name}
                        onChange={(e: any) =>
                          handleEmployeeChange(i, 'name', e.target.value)
                        }
                      />
                    </div>
                  </Col>
                  <Col lg={3} md={6} className="mb-3 m-lg-0">
                    <div className="d-flex align-items-baseline">
                      {isUsingDOB ? (
                        <>
                          <InputLabel>DOB: </InputLabel>
                          <Form.Control
                            placeholder="yyyy-mm-dd"
                            type="date"
                            style={{ width: 160 }}
                            className="bg-white w-100"
                            value={(employee.dob as Date)?.toString()}
                            onChange={(e: any) => {
                              if (e.target.value.length > 10)
                                return e.preventDefault()

                              handleEmployeeChange(i, 'dob', e.target.value)
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <InputLabel>Age: </InputLabel>
                          <EmployeeInput
                            style={{ width: '54px' }}
                            className="bg-white h-100"
                            type="number"
                            placeholder="35"
                            min={0}
                            max={110}
                            value={employee.age}
                            onChange={(e: any) => {
                              if (
                                (Number(e.target.value) >= 0 &&
                                  Number(e.target.value) < 111) ||
                                typeof e.target.value === 'undefined'
                              )
                                handleEmployeeChange(i, 'age', e.target.value)
                            }}
                          />
                        </>
                      )}
                    </div>
                  </Col>
                  <Col lg={2} md={6} className="mb-3 m-lg-0">
                    <div className="d-flex align-items-baseline">
                      <InputLabel>Spouse: </InputLabel>
                      <Form.Control
                        style={{
                          marginRight: 30,
                          width: 110
                        }}
                        className="bg-white h-100  px-2"
                        as="select"
                        value={employee.spouse ? 'yes' : 'no'}
                        onChange={(e: any) =>
                          handleEmployeeChange(
                            i,
                            'spouse',
                            e.target.value === 'yes'
                          )
                        }
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </Form.Control>
                    </div>
                  </Col>
                  <Col lg={2} md={6} className="mb-3 m-lg-0">
                    <div className="d-flex align-items-baseline">
                      <InputLabel>Children: </InputLabel>
                      <EmployeeInput
                        style={{ width: '54px' }}
                        className="bg-white h-100 w-100"
                        type="number"
                        rules={{ required: true }}
                        placeholder="#"
                        min={0}
                        max={60}
                        onChange={(e: any) => {
                          if (
                            (Number(e.target.value) >= 0 &&
                              Number(e.target.value) < 60) ||
                            typeof e.target.value === 'undefined'
                          )
                            handleEmployeeChange(i, 'children', e.target.value)
                        }}
                        value={employee.children}
                      />
                    </div>
                  </Col>
                </EmployeeFirstRow>
                {employee.spouse && (
                  <EmployeeSecondRow>
                    <Col lg={5} md={6}>
                      <EmployeeNumber>Spouse</EmployeeNumber>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-baseline">
                        {isUsingDOB ? (
                          <>
                            <InputLabel>DOB: </InputLabel>
                            <Form.Control
                              type="date"
                              placeholder="yyyy-mm-dd"
                              style={{ width: 160 }}
                              className="bg-white"
                              value={(employee.spouseDob as Date)?.toString()}
                              onChange={(e: any) => {
                                if (e.target.value.length > 10)
                                  return e.preventDefault()

                                handleEmployeeChange(
                                  i,
                                  'spouseDob',
                                  e.target.value
                                )
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <InputLabel>Age: </InputLabel>
                            <EmployeeInput
                              style={{ width: '54px' }}
                              className="bg-white"
                              type="number"
                              placeholder="35"
                              value={employee.spouseAge}
                              min={0}
                              max={110}
                              onChange={(e: any) => {
                                if (
                                  (Number(e.target.value) >= 0 &&
                                    Number(e.target.value) < 111) ||
                                  typeof e.target.value === 'undefined'
                                )
                                  handleEmployeeChange(
                                    i,
                                    'spouseAge',
                                    e.target.value
                                  )
                              }}
                            />
                          </>
                        )}
                      </div>
                    </Col>
                  </EmployeeSecondRow>
                )}
              </Col>
              <Col xs={1}>
                <Dropdown drop="down">
                  <Dropdown.Toggle
                    as={MenuToggle}
                    id={`dropdown-option-${i}`}
                  />
                  <Dropdown.Menu
                    className="py-0"
                    style={{ boxShadow: '5px 5px 15px 5px rgba(0,0,0,5%)' }}
                  >
                    <Dropdown.Item
                      onClick={() => handleDelete(i)}
                      className="fs-16 text-danger"
                    >
                      Remove
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
          </EmployeeWrapper>
        ))}
        <div className="d-flex justify-content-end">
          <Button
            className="bg-white border-primary text-primary fs-16"
            onClick={handleAddEmployee}
          >
            Add
          </Button>
        </div>
      </ListWrapper>
    </EmployeesWrapper>
  )
}

export default Employees
